import { apiUrl, authFetch } from './client';
import { toApiError, type ApiError } from './errors';

/**
 * Отправитель для офлайн-очереди (@dalat/offline). Структурные типы, чтобы пакеты не зависели друг от друга.
 * - 2xx → 'ok'
 * - сеть/5xx/429/401 (refresh не удался из-за сети) → 'retry' (порядок очереди сохраняется)
 * - прочие 4xx → 'drop' + onDrop (бизнес-отказ: already_taken, version_conflict, ...)
 */
export interface QueueActionLike {
  id: string;
  method: string;
  path: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export type QueueSendResult = 'ok' | 'retry' | 'drop';

export function createQueueSender(hooks: {
  onOk?: (action: QueueActionLike, data: unknown) => void;
  onDrop?: (action: QueueActionLike, error: ApiError) => void;
} = {}): (action: QueueActionLike) => Promise<QueueSendResult> {
  return async (action) => {
    const headers = new Headers(action.headers);
    if (!headers.has('Idempotency-Key')) headers.set('Idempotency-Key', action.id);
    const init: RequestInit = { method: action.method, headers };
    if (action.body !== undefined) {
      headers.set('Content-Type', 'application/json');
      init.body = JSON.stringify(action.body);
    }
    let res: Response;
    try {
      res = await authFetch(new Request(apiUrl(action.path), init));
    } catch {
      return 'retry';
    }
    let data: unknown;
    try {
      data = res.status === 204 ? null : await res.json();
    } catch {
      data = null;
    }
    if (res.ok) {
      hooks.onOk?.(action, data);
      return 'ok';
    }
    if (res.status >= 500 || res.status === 429 || res.status === 401 || res.status === 408) return 'retry';
    // 409 idempotency_in_progress — первый запрос ещё выполняется, повторим.
    const err = toApiError(res.status, data);
    if (err.code === 'idempotency_in_progress') return 'retry';
    hooks.onDrop?.(action, err);
    return 'drop';
  };
}
